<script lang="ts" setup>
import { useRoute } from 'vue-router';

import { useMetaProfile } from '@/composables/meta/useMetaProfile';
import { useUserStore } from '@/stores/user/useUserStore';
import { storeToRefs, useWatchQueryParams } from '#imports';

const tabs: OptionValue<AppCollectionTabsTypeProfile>[] = [
    {
        value: 'Collected',
        label: 'Collected'
    },
    {
        value: 'Offers Made',
        label: 'Offers Made'
    },
    {
        value: 'Offers Received',
        label: 'Offers Received'
    },
    // {
    //     value: 'Created',
    //     label: 'Created'
    // },
    {
        value: 'Favorited',
        label: 'Favorited'
    }
    // {
    //     value: 'Activity',
    //     label: 'Activity'
    // }
];
definePageMeta({
    auth: false, // TODO: временно выключено для показа публичного профиля пользователя
    ssr: false
});
const {
    nft,
    QueryWTFApiKey
} = useWTFApi();

type QueryParamsType =
    {
        tab: AppCollectionTabsTypeProfile;
    };

const userStore = useUserStore();

const {
    user
} = storeToRefs(userStore);

const route = useRoute();
const router = useRouter();

const owner = route.params.owner as User['address'];

const alertStore = useAlertsStore();

const showUserUnauthorized = () => {
    alertStore.add({
        title: 'Oops, something went wrong',
        description: 'Please login to your wallet to use the watchlist feature',
        critical: false,
        status: 'type.info'
    });
};

const {
    onChangeQueryParams,
    query
} = useWatchQueryParams<QueryParamsType>({
    tab: 'Collected'
}, `/profile/${owner}`);

if (user.value) {
    useMetaProfile({
        user: user.value,
        route
    });
}

const userWatcher = ref<(() => void) | null>(null);

const handleUserChange = (newAddress: string) => {
    if (newAddress) {
        router.push(`/profile/${newAddress}`);
    }
};

const activateWatcher = () => {
    if (!userWatcher.value) {
        userWatcher.value = watch(
            () => user.value,
            (newAddress) => {
                if (newAddress) handleUserChange(newAddress);
            }
        );
    }
};

const deactivateWatcher = () => {
    if (userWatcher.value) {
        userWatcher.value();
        userWatcher.value = null;
    }
};

onActivated(() => {
    activateWatcher();
});

onDeactivated(() => {
    deactivateWatcher();
});

onMounted(() => {
    activateWatcher();
});

onUnmounted(() => {
    deactivateWatcher();
});

const onChangeTab = (tab: AppCollectionTabsTypeProfile) => {
    onChangeQueryParams({ tab });
};

const disabledTabs = computed(() => !user.value || (user.value && route.params.owner !== user.value) ? ['Favorited'] : []);

const componentsMap = {
    'Collected': defineAsyncComponent(() => import('@/components/features/profile/ProfileCollected.vue')),
    'Offers Made': defineAsyncComponent(() => import('~/components/features/profile/ProfileMadeOffers/ProfileMadeOffers.vue')),
    'Offers Received': defineAsyncComponent(() => import('~/components/features/profile/ProfileReceivedOffers/ProfileReceivedOffers.vue')),
    'Favorited': defineAsyncComponent(() => import('~/components/features/WatchList.vue'))
};

</script>

<template>
  <div class="display-flex flex-column grow">
    <ProfileOverview :address="owner" />
    <Tabs :active-tab="query.tab"
          :tabs="tabs"
          :disabled-tabs="disabledTabs"
          @on-change="(tab) => onChangeTab(tab as AppCollectionTabsTypeProfile)"
          @handler-disabled-tabs="showUserUnauthorized"
          class="overflow-auto"
    />
    <section aria-label="main content"
             class="grow"
    >
      <Suspense>
        <template #default>
          <KeepAlive>
            <component v-for="(Comp, key) in componentsMap"
                       :is="Comp"
                       :key="`${key}-${owner}`"
                       :owner="owner"
                       :loader-size="key === 'Favorited' ? 10 : undefined"
                       v-show="query.tab === key"
            />
          </KeepAlive>
        </template>
        <template #fallback>
          <LoaderDots />
        </template>
      </Suspense>
    </section>
  </div>
</template>

<style lang="scss" scoped>
:deep(.nft-skeleton) {
  height: 261px;
}
</style>
